import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../../shared/api';
import { DateInput } from '../../shared/DateInput';
import { EmployeePicker } from './EmployeePicker';
import type { User } from '../../types/biApp';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';
const RED = '#b14040';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: 999, border: '1px solid ' + (active ? BROWN : '#e0d6c0'),
    background: active ? BROWN : '#fff', color: active ? '#fff' : BROWN, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem',
});
const inp: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem', width: '100%' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, borderBottom: '2px solid #efe6d2' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: '0.86rem', borderBottom: '1px solid #f3ecdb' };
const tdNum: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

type Absence = { go: number; bol42: number; bol42p: number };
type Branch = {
    department_id: number; name: string; region: string | null; manager: string | null;
    open_hours_week: number; required_staff: number; current_staff: number; gap_staff: number;
    missing_hours_week: number; missing_hours_month: number; absences: Absence;
};
type CoverageResp = { period: { from: string; to: string }; branches: Branch[]; totals: { gap_staff: number; missing_hours_week: number; missing_hours_month: number; branches: number } };

type Move = { employee_id: number; employee: string; from: string; to: string; region: string };
type Unfilled = { name: string; region: string; still_short: number };
type SuggestResp = { period: { from: string; to: string }; moves: Move[]; unfilled: Unfilled[]; summary: { moves: number; unfilled: number }; note: string };

type GenWorker = { employee_id: number; name: string; actual: 'in' | 'no'; lent?: boolean; from?: string | null };
type GenShift = { need: number; unfilled: number; range: string; workers: GenWorker[] };
type GenDay = { date: string; weekday: number; closed?: boolean; required?: number; morning?: GenShift; afternoon?: GenShift };
type GenBranch = { department_id: number; name: string; region: string | null; week: GenDay[] };
type GenResp = { period: { from: string; to: string }; branches: GenBranch[]; totals: { unfilled: number }; note: string };

type AbsenceRow = {
    id: number; employee_id: number; employee_name: string; department_name: string | null;
    type: string; date_from: string; date_to: string | null; source: string; duration_days: number; bucket: string;
};
type EmployeeRow = { id: number; first_name: string | null; last_name: string | null };

export function CoverageTab({ token, locale }: { token: string; locale: Locale; user: User }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    type Sub = 'coverage' | 'hours' | 'absences';
    const [sub, setSub] = useState<Sub>('coverage');

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                <button type="button" style={tabBtn(sub === 'coverage')} onClick={() => setSub('coverage')}>{t('Pokrivenost', 'Coverage')}</button>
                <button type="button" style={tabBtn(sub === 'hours')} onClick={() => setSub('hours')}>{t('Radno vrijeme', 'Working hours')}</button>
                <button type="button" style={tabBtn(sub === 'absences')} onClick={() => setSub('absences')}>{t('Bolovanja', 'Sick leave')}</button>
            </div>
            {sub === 'coverage' ? <CoveragePanel token={token} locale={locale} />
                : sub === 'hours' ? <BranchHoursPanel token={token} locale={locale} />
                : <AbsencesPanel token={token} locale={locale} />}
        </div>
    );
}

// ── Radno vrijeme poslovnice (editabilno → "Maksimalno") ─────────────────────
type BranchLite = { department_id: number; name: string; region: string | null };
type DayRow = { weekday: number; open_time: string; close_time: string; required_staff: number };
const WD = ['', 'PON', 'UTO', 'SRI', 'ČET', 'PET', 'SUB', 'NED'];
const hm = (s: string | null) => (s ? s.slice(0, 5) : '');

function BranchHoursPanel({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [branches, setBranches] = useState<BranchLite[]>([]);
    const [dept, setDept] = useState<number | null>(null);
    const [days, setDays] = useState<DayRow[]>(() => Array.from({ length: 7 }, (_, i) => ({ weekday: i + 1, open_time: '', close_time: '', required_staff: 0 })));
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);

    useEffect(() => {
        api<{ branches: BranchLite[] }>('/hr/coverage', token).then((r) => setBranches(r.branches)).catch(() => setBranches([]));
    }, [token]);

    const loadDept = useCallback((id: number) => {
        setMsg(null);
        api<{ rows: { weekday: number; open_time: string | null; close_time: string | null; required_staff: number }[] }>(`/hr/branch-hours?department_id=${id}`, token)
            .then((r) => {
                const base: DayRow[] = Array.from({ length: 7 }, (_, i) => ({ weekday: i + 1, open_time: '', close_time: '', required_staff: 0 }));
                r.rows.forEach((row) => { const d = base[row.weekday - 1]; if (d) { d.open_time = hm(row.open_time); d.close_time = hm(row.close_time); d.required_staff = row.required_staff; } });
                setDays(base);
            }).catch(() => undefined);
    }, [token]);

    const pick = (id: number | null) => { setDept(id); if (id) loadDept(id); };
    const setDay = (i: number, k: keyof DayRow, v: string) => setDays((ds) => ds.map((d, idx) => idx === i ? { ...d, [k]: k === 'required_staff' ? (Number(v) || 0) : v } : d));

    const derive = async () => {
        if (!dept) return;
        setBusy(true); setMsg(null);
        try {
            const r = await mutate<{ days: DayRow[] }>('/hr/branch-hours/derive', token, 'POST', { department_id: dept });
            const base: DayRow[] = Array.from({ length: 7 }, (_, i) => ({ weekday: i + 1, open_time: '', close_time: '', required_staff: 0 }));
            r.days.forEach((row) => { const d = base[row.weekday - 1]; if (d) { d.open_time = hm(row.open_time); d.close_time = hm(row.close_time); d.required_staff = row.required_staff; } });
            setDays(base);
            setMsg(t('Predloženo iz radnog vremena poslovnice — pregledaj i spremi.', 'Suggested from branch hours — review and save.'));
        } catch (e) { setMsg(String((e as Error)?.message ?? e)); } finally { setBusy(false); }
    };
    const save = async () => {
        if (!dept) return;
        setBusy(true); setMsg(null);
        try {
            await mutate('/hr/branch-hours/bulk', token, 'POST', { department_id: dept, days: days.map((d) => ({ weekday: d.weekday, open_time: d.open_time || null, close_time: d.close_time || null, required_staff: d.required_staff })) });
            setMsg(t('Spremljeno.', 'Saved.'));
        } catch (e) { setMsg(String((e as Error)?.message ?? e)); } finally { setBusy(false); }
    };

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Radno vrijeme poslovnice (Maksimalno)', 'Branch working hours (Required)')}</strong>
                <p className="muted" style={{ fontSize: '0.78rem', margin: '6px 0 12px' }}>{t('Po danu: otvaranje, zatvaranje i potreban broj prodavača. Prazni sati = zatvoreno.', 'Per day: open, close and required staff. Empty = closed.')}</p>
                <select style={{ ...inp, maxWidth: 460 }} value={dept ?? ''} onChange={(e) => pick(e.target.value ? Number(e.target.value) : null)}>
                    <option value="">{t('— odaberi poslovnicu —', '— select branch —')}</option>
                    {branches.map((b) => <option key={b.department_id} value={b.department_id}>{b.name}{b.region ? ` · ${b.region}` : ''}</option>)}
                </select>
            </div>

            {dept ? (
                <div style={card}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr><th style={th}>{t('Dan', 'Day')}</th><th style={th}>{t('Otvaranje', 'Open')}</th><th style={th}>{t('Zatvaranje', 'Close')}</th><th style={{ ...th, textAlign: 'right' }}>{t('Posada', 'Staff')}</th></tr></thead>
                            <tbody>
                                {days.map((d, i) => (
                                    <tr key={d.weekday}>
                                        <td style={{ ...td, fontWeight: 700, color: BROWN }}>{WD[d.weekday]}</td>
                                        <td style={td}><input type="time" style={{ ...inp, width: 120 }} value={d.open_time} onChange={(e) => setDay(i, 'open_time', e.target.value)} /></td>
                                        <td style={td}><input type="time" style={{ ...inp, width: 120 }} value={d.close_time} onChange={(e) => setDay(i, 'close_time', e.target.value)} /></td>
                                        <td style={{ ...td, textAlign: 'right' }}><input type="number" min={0} style={{ ...inp, width: 70, textAlign: 'right' }} value={d.required_staff} onChange={(e) => setDay(i, 'required_staff', e.target.value)} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {msg ? <div style={{ fontSize: '0.82rem', color: msg === 'Spremljeno.' || msg === 'Saved.' ? '#3f7a3f' : BROWN, marginTop: 10 }}>{msg}</div> : null}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                        <button type="button" style={tabBtn(false)} disabled={busy} onClick={derive}>⟳ {t('Iz radnog vremena poslovnice', 'From branch hours')}</button>
                        <button type="button" style={tabBtn(true)} disabled={busy} onClick={save}>{busy ? t('Spremam…', 'Saving…') : t('Spremi', 'Save')}</button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

// ── Pokrivenost (manjak po poslovnici/regiji) ────────────────────────────────
function CoveragePanel({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [data, setData] = useState<CoverageResp | null>(null);
    const [err, setErr] = useState<string | null>(null);

    const load = useCallback(() => {
        setData(null); setErr(null);
        const qs: string[] = [];
        if (from) qs.push(`from=${from}`);
        if (to) qs.push(`to=${to}`);
        api<CoverageResp>('/hr/coverage' + (qs.length ? `?${qs.join('&')}` : ''), token)
            .then(setData)
            .catch((e) => setErr(String(e?.message ?? e)));
    }, [token, from, to]);
    useEffect(load, [load]);

    const grouped = useMemo(() => {
        const m = new Map<string, Branch[]>();
        (data?.branches ?? []).forEach((b) => {
            const k = b.region ?? '—';
            if (!m.has(k)) m.set(k, []);
            m.get(k)!.push(b);
        });
        return Array.from(m.entries());
    }, [data]);

    const [gen, setGen] = useState<GenResp | null>(null);
    const [genBusy, setGenBusy] = useState(false);
    const generate = async () => {
        setGenBusy(true); setGen(null);
        const q = from ? `?from=${from}` : '';
        try { setGen(await api<GenResp>('/hr/coverage/generate' + q, token)); }
        catch (e) { setGen({ period: { from: '', to: '' }, branches: [], totals: { unfilled: 0 }, note: String((e as Error)?.message ?? e) }); }
        finally { setGenBusy(false); }
    };
    const renderShift = (s?: GenShift) => {
        if (!s) return <span className="muted">—</span>;
        return (
            <div>
                <div style={{ fontSize: '0.72rem', color: '#8a7a55' }}>{s.range}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                    {s.workers.map((w, i) => (
                        <span key={i} title={(w.actual === 'in' ? t('evidentiran', 'recorded') : t('nije evidentiran', 'not recorded')) + (w.lent ? ` · ${t('posuđen iz', 'lent from')} ${w.from ?? '?'}` : '')}
                            style={{ fontSize: '0.78rem', padding: '2px 8px', borderRadius: 999, background: w.actual === 'in' ? '#e4f3e9' : '#f3ece0', color: w.actual === 'in' ? '#1f6e45' : BROWN, border: '1px solid ' + (w.lent ? GOLD : (w.actual === 'in' ? '#bfe3cd' : '#e6dcc4')) }}>
                            {w.actual === 'in' ? '✓' : '·'} {w.lent ? '↪ ' : ''}{w.name}
                        </span>
                    ))}
                    {s.unfilled > 0 ? <span style={{ fontSize: '0.78rem', padding: '2px 8px', borderRadius: 999, background: '#f7e0e0', color: RED, fontWeight: 600 }}>−{s.unfilled} {t('fali', 'short')}</span> : null}
                </div>
            </div>
        );
    };
    const showSaved = async () => {
        setGenBusy(true); setGen(null); setSaveMsg(null);
        const q = from ? `?from=${from}` : '';
        try { setGen(await api<GenResp>('/hr/coverage/saved' + q, token)); }
        catch (e) { setGen({ period: { from: '', to: '' }, branches: [], totals: { unfilled: 0 }, note: String((e as Error)?.message ?? e) }); }
        finally { setGenBusy(false); }
    };
    const [saveMsg, setSaveMsg] = useState<string | null>(null);
    const saveProposal = async () => {
        setSaveMsg(null);
        const q = from ? `?from=${from}` : '';
        try { const r = await mutate<{ saved: number }>('/hr/coverage/save-proposal' + q, token, 'POST', {}); setSaveMsg(t(`Spremljeno u raspored: ${r.saved} unosa.`, `Saved to schedule: ${r.saved} entries.`)); }
        catch (e) { setSaveMsg(String((e as Error)?.message ?? e)); }
    };

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
                    <strong style={{ color: BROWN }}>{t('Pokrivenost poslovnica', 'Branch coverage')}{data ? ` — ${data.period.from} → ${data.period.to}` : ''}</strong>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                        <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Od', 'From')}<div style={{ width: 150 }}><DateInput value={from} onChange={setFrom} /></div></label>
                        <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Do', 'To')}<div style={{ width: 150 }}><DateInput value={to} onChange={setTo} /></div></label>
                    </div>
                </div>
                {data ? (
                    <div style={{ display: 'flex', gap: 18, marginTop: 12, flexWrap: 'wrap' }}>
                        <Kpi label={t('Manjak ljudi', 'Staff gap')} value={String(data.totals.gap_staff)} danger={data.totals.gap_staff > 0} />
                        <Kpi label={t('Manjak sati / tjedan', 'Missing h / week')} value={String(data.totals.missing_hours_week)} danger={data.totals.missing_hours_week > 0} />
                        <Kpi label={t('Manjak sati / mjesec', 'Missing h / month')} value={String(data.totals.missing_hours_month)} danger={data.totals.missing_hours_month > 0} />
                        <Kpi label={t('Poslovnica', 'Branches')} value={String(data.totals.branches)} />
                    </div>
                ) : null}
            </div>

            <div style={{ ...card, borderColor: GOLD }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <strong style={{ color: BROWN }}>{t('Prijedlog rasporeda (tjedan)', 'Schedule proposal (week)')}{gen && gen.period.from ? ` — ${gen.period.from} → ${gen.period.to}` : ''}</strong>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" style={tabBtn(true)} disabled={genBusy} onClick={generate}>
                            {genBusy ? t('Generiram…', 'Generating…') : '🗓️ ' + t('Generiraj prijedlog rasporeda', 'Generate schedule proposal')}
                        </button>
                        {gen && gen.branches.length ? <button type="button" style={tabBtn(false)} onClick={saveProposal}>💾 {t('Spremi u raspored', 'Save to schedule')}</button> : null}
                        <button type="button" style={tabBtn(false)} disabled={genBusy} onClick={showSaved}>📋 {t('Spremljeni raspored', 'Saved schedule')}</button>
                    </div>
                </div>
                {saveMsg ? <div style={{ fontSize: '0.82rem', color: saveMsg.startsWith('Spremljeno') || saveMsg.startsWith('Saved') ? '#3f7a3f' : RED, marginTop: 8 }}>{saveMsg}</div> : null}
                {gen ? (
                    <div style={{ marginTop: 12 }}>
                        <div className="muted" style={{ fontSize: '0.76rem', marginBottom: 10 }}>{gen.note}{gen.totals.unfilled > 0 ? ` · ${t('nepokriveno', 'unfilled')}: ${gen.totals.unfilled}` : ''}</div>
                        {gen.branches.filter((b) => b.week.some((d) => !d.closed)).map((b) => (
                            <div key={b.department_id} style={{ marginBottom: 16 }}>
                                <div style={{ fontWeight: 700, color: BROWN, marginBottom: 4 }}>{b.name} <span className="muted" style={{ fontWeight: 400 }}>· {b.region ?? ''}</span></div>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead><tr>
                                            <th style={{ ...th, width: 90 }}>{t('Dan', 'Day')}</th>
                                            <th style={th}>{t('Jutarnja', 'Morning')}</th>
                                            <th style={th}>{t('Popodnevna', 'Afternoon')}</th>
                                        </tr></thead>
                                        <tbody>
                                            {b.week.map((d, i) => d.closed ? null : (
                                                <tr key={i}>
                                                    <td style={{ ...td, fontWeight: 600 }}>{WD[d.weekday]} <span className="muted" style={{ fontWeight: 400 }}>{d.date.slice(5)}</span></td>
                                                    <td style={td}>{renderShift(d.morning)}</td>
                                                    <td style={td}>{renderShift(d.afternoon)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                        <div className="muted" style={{ fontSize: '0.74rem' }}>{t('✓ = radnik je evidentiran taj dan · · = predložen ali nema evidencije.', '✓ = recorded that day · · = proposed but no record.')}</div>
                    </div>
                ) : null}
            </div>

            <div style={card}>
                {err ? <div style={{ color: RED, fontSize: '0.82rem' }}>⚠ {err}</div> : null}
                {!data && !err ? <div className="muted">{t('Učitavam…', 'Loading…')}</div> : null}
                {data ? (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr>
                                <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Radni sati/tj.', 'Open h/wk')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Treba', 'Req.')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Trenutno', 'Cur.')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Manjak', 'Gap')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Manjak h/tj.', 'Miss h/wk')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>g.o.</th>
                                <th style={{ ...th, textAlign: 'right' }}>bol ≤42</th>
                                <th style={{ ...th, textAlign: 'right' }}>bol &gt;42</th>
                            </tr></thead>
                            <tbody>
                                {grouped.map(([region, rows]) => (
                                    <React.Fragment key={region}>
                                        <tr><td colSpan={9} style={{ ...td, background: '#faf6ec', fontWeight: 700, color: BROWN }}>
                                            {region} · {rows[0]?.manager ?? '—'}
                                        </td></tr>
                                        {rows.map((b) => (
                                            <tr key={b.department_id}>
                                                <td style={td}>{b.name}</td>
                                                <td style={tdNum}>{b.open_hours_week}</td>
                                                <td style={tdNum}>{b.required_staff}</td>
                                                <td style={tdNum}>{b.current_staff}</td>
                                                <td style={{ ...tdNum, color: b.gap_staff > 0 ? RED : undefined, fontWeight: b.gap_staff > 0 ? 700 : 400 }}>{b.gap_staff || '—'}</td>
                                                <td style={{ ...tdNum, color: b.missing_hours_week > 0 ? RED : undefined }}>{b.missing_hours_week || '—'}</td>
                                                <td style={tdNum}>{b.absences.go || '—'}</td>
                                                <td style={tdNum}>{b.absences.bol42 || '—'}</td>
                                                <td style={tdNum}>{b.absences.bol42p || '—'}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                        <div className="muted" style={{ fontSize: '0.74rem', marginTop: 10 }}>
                            {t('Manjak sati je procjena (manjak ljudi × tjedni fond do 40 h). Precizni raspoređeni sati dolaze u fazi 2.',
                               'Missing hours is an estimate (staff gap × weekly cap up to 40 h). Exact scheduled hours come in phase 2.')}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function Kpi({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
    return (
        <div style={{ minWidth: 130 }}>
            <div style={{ fontSize: '0.72rem', color: BROWN, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: danger ? RED : BROWN }}>{value}</div>
        </div>
    );
}

// ── Bolovanja (HR unos po doznaci/dojavi) ────────────────────────────────────
function AbsencesPanel({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<AbsenceRow[] | null>(null);
    const [onlyActive, setOnlyActive] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [f, setF] = useState({ employee_id: '', date_from: '', date_to: '', source: 'doznaka', note: '' });
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setRows(null); setErr(null);
        api<{ rows: AbsenceRow[] }>(`/hr/absences${onlyActive ? '?active=1' : ''}`, token)
            .then((r) => setRows(r.rows))
            .catch((e) => { setErr(String(e?.message ?? e)); setRows([]); });
    }, [token, onlyActive]);
    useEffect(load, [load]);

    const add = async () => {
        setErr(null);
        if (!f.employee_id || !f.date_from) { setErr(t('Djelatnik i datum od su obavezni.', 'Employee and start date required.')); return; }
        setBusy(true);
        try {
            await mutate('/hr/absences', token, 'POST', {
                employee_id: Number(f.employee_id), date_from: f.date_from,
                date_to: f.date_to || null, source: f.source, note: f.note || null,
            });
            setF({ employee_id: '', date_from: '', date_to: '', source: 'doznaka', note: '' });
            load();
        } catch (e) { setErr(String((e as Error)?.message ?? e)); }
        finally { setBusy(false); }
    };

    const close = async (id: number) => {
        try { await mutate(`/hr/absences/${id}/close`, token, 'POST', {}); load(); }
        catch (e) { setErr(String((e as Error)?.message ?? e)); }
    };

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ ...card, borderColor: GOLD }}>
                <strong style={{ color: BROWN }}>{t('Unos bolovanja (doznaka/dojava)', 'Record sick leave')}</strong>
                <div style={{ display: 'grid', gap: 10, marginTop: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', maxWidth: 880 }}>
                    <label style={{ fontSize: '0.8rem', color: BROWN, gridColumn: '1 / -1' }}>{t('Djelatnik', 'Employee')}
                        <EmployeePicker token={token} locale={locale} value={f.employee_id ? Number(f.employee_id) : null} onChange={(id) => setF({ ...f, employee_id: id ? String(id) : '' })} />
                    </label>
                    <label style={{ fontSize: '0.8rem', color: BROWN }}>{t('Od', 'From')}<DateInput value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} /></label>
                    <label style={{ fontSize: '0.8rem', color: BROWN }}>{t('Do (opcionalno)', 'To (optional)')}<DateInput value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} /></label>
                    <label style={{ fontSize: '0.8rem', color: BROWN }}>{t('Izvor', 'Source')}
                        <select style={inp} value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}>
                            <option value="doznaka">{t('doznaka', 'sick note')}</option>
                            <option value="dojava">{t('dojava', 'notification')}</option>
                        </select>
                    </label>
                    <label style={{ fontSize: '0.8rem', color: BROWN, gridColumn: '1 / -1' }}>{t('Napomena', 'Note')}<input style={inp} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></label>
                </div>
                {err ? <div style={{ color: RED, fontSize: '0.82rem', marginTop: 10 }}>⚠ {err}</div> : null}
                <div style={{ marginTop: 12 }}>
                    <button type="button" style={tabBtn(true)} disabled={busy} onClick={add}>{busy ? t('Spremam…', 'Saving…') : '＋ ' + t('Unesi bolovanje', 'Add sick leave')}</button>
                </div>
            </div>

            <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <strong style={{ color: BROWN }}>{t('Bolovanja', 'Sick leave')} {rows ? `(${rows.length})` : ''}</strong>
                    <label style={{ fontSize: '0.8rem', color: BROWN, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />{t('samo otvorena', 'open only')}
                    </label>
                </div>
                {rows === null ? <div className="muted">{t('Učitavam…', 'Loading…')}</div> : rows.length === 0 ? (
                    <div className="muted">{t('Nema zapisa.', 'No records.')}</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr>
                                <th style={th}>{t('Djelatnik', 'Employee')}</th>
                                <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                <th style={th}>{t('Od', 'From')}</th>
                                <th style={th}>{t('Do', 'To')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Dana', 'Days')}</th>
                                <th style={th}>{t('Kategorija', 'Bucket')}</th>
                                <th style={th}></th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id}>
                                        <td style={td}>{r.employee_name}</td>
                                        <td style={td}>{r.department_name ?? '—'}</td>
                                        <td style={td}>{r.date_from}</td>
                                        <td style={td}>{r.date_to ?? <span style={{ color: GOLD, fontWeight: 700 }}>{t('otvoreno', 'open')}</span>}</td>
                                        <td style={tdNum}>{r.duration_days}</td>
                                        <td style={td}>{r.bucket === 'bol42p' ? <span style={{ color: RED, fontWeight: 700 }}>&gt;42</span> : r.bucket === 'bol42' ? '≤42' : r.bucket}</td>
                                        <td style={td}>{!r.date_to ? <button type="button" style={tabBtn(false)} onClick={() => close(r.id)}>{t('Zatvori', 'Close')}</button> : null}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
